// ── Pure helper: resolve Polymarket order tokens from VenueBooks ─
//
// Extracted from TradeModal so the dispatch contract is testable
// without React. Given the currently-selected book (the team the user
// clicked) and the opposite team's book, returns the YES + NO outcome
// tokens the relay should target.
//
// Two cases collapse into one path:
//   - Moneyline: away/home are DIFFERENT books. NO = the OTHER team's
//     YES outcome token = otherSideBook.tokenId.
//   - Spread/total: away/home share ONE underlying book (synthesize.ts
//     puts the same VenueBook in both slots). NO has to come from
//     within the same book = book.noTokenId.
//
// We prefer book.noTokenId when present (covers spread/total + the
// new moneyline plumbing) and fall back to otherSideBook?.tokenId
// (the original moneyline cross-team trick) for frames that
// pre-date the noTokenId plumbing.
//
// Returns null tokens (not undefined) so callers can use truthy
// checks uniformly.

import type { VenueBook } from './types';

export interface PolyOrderTokens {
  yes: string | null;
  no: string | null;
}

export function resolvePolyOrderTokens(
  book: VenueBook | undefined,
  otherSideBook: VenueBook | undefined,
): PolyOrderTokens {
  const yes = book?.tokenId ?? null;
  // Fallback to opposite team's book.tokenId ONLY when the two books
  // are different references (the moneyline shape). When synthesize.ts
  // puts the same VenueBook reference in both slots (spread/total),
  // the fallback would return the SAME token as YES — a NO bet would
  // wrongly place a YES order. Return null instead so the caller
  // surfaces the "no NO token" error and the trade is blocked.
  const fallbackNo =
    book && otherSideBook && book !== otherSideBook
      ? (otherSideBook.tokenId ?? null)
      : null;
  const no = book?.noTokenId ?? fallbackNo;
  return { yes, no };
}
